import type {
  NewsFeedFilterCategory,
  NewsFeedSidebarFilterItem,
} from "@/data-access/news-feeds/queries/news-feed-sidebar-filters-queries";
import type { StateCreator } from "zustand";
import type { NewsFeedFiltersSlice } from "./newsFeed-filter.slice";

export type NewsFeedFiltersActions = {
  /**
   * Sets loading state for a specific filter category
   */
  setFilterLoading: (
    category: NewsFeedFilterCategory,
    isLoading: boolean,
  ) => void;

  /**
   * Sets error state for a specific filter category
   */
  setFilterError: (
    category: NewsFeedFilterCategory,
    error: string | null,
  ) => void;

  /**
   * Updates filter data for a specific category
   */
  updateFilterData: (
    category: NewsFeedFilterCategory,
    data: NewsFeedSidebarFilterItem[],
  ) => void;

  /**
   * Sets both loading state and data for a filter category
   */
  setFilterCategory: (
    category: NewsFeedFilterCategory,
    data: NewsFeedSidebarFilterItem[],
    isLoading?: boolean,
  ) => void;

  /**
   * Upserts category with lightweight change detection to avoid redundant state updates
   */
  upsertFilterCategory: (
    category: NewsFeedFilterCategory,
    next: { isLoading: boolean; data?: NewsFeedSidebarFilterItem[] },
  ) => void;

  /**
   * Clears all filter data
   */
  clearAllFilters: () => void;

  /**
   * Removes a specific filter category
   */
  removeFilterCategory: (category: NewsFeedFilterCategory) => void;

  /**
   * Sets the expansion state of a filter category
   */
  setCategoryExpansion: (
    category: NewsFeedFilterCategory,
    expanded: boolean,
  ) => void;
};

export const createNewsFeedFiltersActions: StateCreator<
  NewsFeedFiltersSlice,
  [["zustand/devtools", never]],
  [],
  NewsFeedFiltersActions
> = (set, get, _api) => ({
  setFilterLoading: (category: NewsFeedFilterCategory, isLoading: boolean) => {
    const currentFilters = get().filters;
    const newFilters = new Map(currentFilters);

    const existing = newFilters.get(category);
    newFilters.set(category, {
      isLoading,
      data: existing?.data || [],
      error: existing?.error || null,
    });

    set(
      { filters: newFilters },
      false,
      `[User] News Feed Filters: Set loading state for ${category}`,
    );
  },

  setFilterError: (category: NewsFeedFilterCategory, error: string | null) => {
    const currentFilters = get().filters;
    const newFilters = new Map(currentFilters);

    const existing = newFilters.get(category);
    newFilters.set(category, {
      isLoading: existing?.isLoading || false,
      data: existing?.data || [],
      error,
    });

    set(
      { filters: newFilters },
      false,
      `[User] News Feed Filters: Set error state for ${category}`,
    );
  },

  updateFilterData: (
    category: NewsFeedFilterCategory,
    data: NewsFeedSidebarFilterItem[],
  ) => {
    const currentFilters = get().filters;
    const newFilters = new Map(currentFilters);

    newFilters.set(category, {
      isLoading: false,
      data,
      error: null,
    });

    set(
      { filters: newFilters },
      false,
      `[User] News Feed Filters: Update data for ${category}`,
    );
  },

  setFilterCategory: (
    category: NewsFeedFilterCategory,
    data: NewsFeedSidebarFilterItem[],
    isLoading = false,
  ) => {
    const currentFilters = get().filters;
    const newFilters = new Map(currentFilters);

    newFilters.set(category, {
      isLoading,
      data,
      error: null,
    });

    set(
      { filters: newFilters },
      false,
      `[User] News Feed Filters: Set category ${category}`,
    );
  },

  upsertFilterCategory: (
    category: NewsFeedFilterCategory,
    next: { isLoading: boolean; data?: NewsFeedSidebarFilterItem[] },
  ) => {
    set(
      (state) => {
        const current = state.filters.get(category);
        const nextData = next.data ?? current?.data ?? [];
        const sameLoading = current?.isLoading === next.isLoading;
        const prevData = current?.data ?? [];
        const sameData =
          prevData.length === nextData.length &&
          prevData.every(
            (p, i) =>
              p.category === nextData[i]?.category &&
              p.categoryLabel === nextData[i]?.categoryLabel &&
              p.total === nextData[i]?.total,
          );
        if (sameLoading && sameData) return state as NewsFeedFiltersSlice;

        const copy = new Map(state.filters);
        copy.set(category, {
          isLoading: next.isLoading,
          data: nextData,
          error: null,
        });
        return { filters: copy } as NewsFeedFiltersSlice;
      },
      false,
      `[User] News Feed Filters: Upsert category ${category}`,
    );
  },

  clearAllFilters: () => {
    set(
      { filters: new Map() },
      false,
      "[User] News Feed Filters: Clear all filters",
    );
  },

  removeFilterCategory: (category: NewsFeedFilterCategory) => {
    const currentFilters = get().filters;
    const newFilters = new Map(currentFilters);

    newFilters.delete(category);

    set(
      { filters: newFilters },
      false,
      `[User] News Feed Filters: Remove category ${category}`,
    );
  },

  setCategoryExpansion: (
    category: NewsFeedFilterCategory,
    expanded: boolean,
  ) => {
    const currentExpanded = get().expandedCategories;
    const newExpanded = new Set(currentExpanded);

    if (expanded) {
      newExpanded.add(category);
    } else {
      newExpanded.delete(category);
    }

    set(
      { expandedCategories: newExpanded },
      false,
      `[User] News Feed Filters: Set expansion for ${category} to ${expanded}`,
    );
  },
});
